import { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator } from 'react-native';

import { PrimaryButton } from './Button';
import KidsInput, { Kid } from './KidsInput';
import SelectInput from './SelectInput';
import { Divider } from './Themed';
import { ViewSelectedWrapper } from './booking_calendar/ViewSelectedButton';
import { CampItemResponse } from '../api/camps';
import Colors from '../constants/Colors';
import { useAuthenticate } from '../contexts/authFlow';
import { useIsLoggedIn } from '../contexts/authentication';
import { useCheckoutState, useCheckoutStateUpdate } from '../contexts/checkout';
import useAppNavigation from '../hooks/useAppNavigation';
import useCampsQuery from '../hooks/useCampsQuery';
import useColorScheme from '../hooks/useColorScheme';
import BookingCalendar from '../native/components/BookingCalendar';
import { getAgeInMonths } from '../utils/date';

type DateRange = {
  id?: string;
  startDate: string;
  endDate: string;
  dates: string[];
};

export function useCurrentCampSelection(
  locationId: number,
  preselectedCampId?: number | null
) {
  const campsQuery = useCampsQuery(locationId);
  const [selectedCampId, setSelectedCampId] = useState<number | null>(
    preselectedCampId ?? null
  );

  useEffect(() => {
    if (
      campsQuery.data?.length &&
      campsQuery.data.length > 0 &&
      !selectedCampId
    ) {
      setSelectedCampId(campsQuery.data[0]!.id);
    }
  }, [campsQuery.data]);

  const currentCampDetails = useMemo(
    () => campsQuery.data?.find((camp) => camp.id === selectedCampId),
    [campsQuery.data, selectedCampId]
  );

  return {
    isLoading: campsQuery.isLoading,
    data: campsQuery.data,
    selectedCampId,
    setSelectedCampId,
    currentCampDetails,
  };
}

export default function CampCalendarContainer({
  locationId,
  submitLabel,
  onSubmit,
  locationNameId,
  kids,
  onKidChosenChange,
}: {
  locationId: number;
  locationNameId: string;
  submitLabel?: string;
  onSubmit?: () => void;
  kids: Kid[];
  onKidChosenChange: (index: number) => void;
}) {
  const { campDetails } = useCheckoutState();

  const { data, isLoading, setSelectedCampId, currentCampDetails } =
    useCurrentCampSelection(locationId, campDetails?.id ?? null);

  const { navigate } = useAppNavigation();

  const isLoggedIn = useIsLoggedIn();
  const authenticate = useAuthenticate();
  const onLoggedOutEdit = () => authenticate();

  const updateCheckoutState = useCheckoutStateUpdate();

  const [selectedCamp, setSelectedCamp] = useState<CampItemResponse | null>(
    campDetails ?? null
  );

  const kidsWithEligibility = useMemo(
    () => kids.map((kid) => setCampEligibility(kid, currentCampDetails)),
    [kids, currentCampDetails]
  );

  const eligibleKidsAmount = kidsWithEligibility.length;

  const currentCampDateRanges = currentCampDetails
    ? [
        {
          startDate: currentCampDetails.start_date,
          endDate: currentCampDetails.end_date,
          dates: currentCampDetails.dates,
        },
      ]
    : [];

  const handleSubmit = () => {
    if (!isLoggedIn) throw new Error('Not logged in');
    if (!selectedCamp) throw new Error('No camp selected');

    updateCheckoutState({
      campDetails: selectedCamp,
      bookingMode: 'camp',
      locationNameId,
      kids: kidsWithEligibility,
    });

    if (onSubmit) {
      onSubmit();
    } else {
      navigate('ConfirmBooking');
    }
  };

  return (
    <>
      <KidsInput
        onChange={onKidChosenChange}
        kids={kidsWithEligibility}
        isFetching={isLoading}
        errorStatus={null}
        onLoggedOutEdit={onLoggedOutEdit}
      />
      <Divider style={{ marginVertical: 10 }} />
      <SelectInput
        options={data ?? []}
        onChange={(option) => {
          setSelectedCampId(option.id);
          setSelectedCamp(null);
        }}
        value={currentCampDetails?.name || ''}
        valueSelector={(option) => option.name}
        style={{ marginBottom: 0, marginTop: 10 }}
      />
      {currentCampDetails ? (
        <CampContent
          dateRanges={currentCampDateRanges}
          onSelect={() =>
            eligibleKidsAmount === 0
              ? undefined
              : setSelectedCamp(currentCampDetails)
          }
          isFetching={isLoading}
          isCampSelected={!!selectedCamp}
        />
      ) : null}

      <ViewSelectedWrapper>
        <PrimaryButton
          title={submitLabel || 'Book'}
          style={{ marginTop: 10, height: 50, flex: 1 }}
          disabled={!selectedCamp || eligibleKidsAmount === 0}
          // disabled={
          //   (isLoggedIn && kids.length === 0) ||
          //   !Object.keys(chosenSlots).length ||
          //   !anyAvailableChosen()
          // }
          onPress={isLoggedIn ? handleSubmit : onLoggedOutEdit}
          testID="book-button"
        />
        {/* {selectedCamp ? (
          <ViewSelectedButton
            showSelected={showSelected}
            amount={1}
            customText="View Selected Camp Details"
          />
        ) : null} */}
      </ViewSelectedWrapper>
    </>
  );
}
export function setCampEligibility(kid: Kid, camp?: CampItemResponse): Kid {
  if (!camp) return kid;

  const ageOnCampDate = getAgeInMonths(
    kid.birthdate,
    new Date(camp.start_date)
  );

  const isEligible =
    ageOnCampDate >= camp.age_from && ageOnCampDate <= camp.age_to;
  return {
    ...kid,
    isEligible,
  };
}

export function CampContent({
  dateRanges,
  daysAllowed = [],
  isFetching = false,
  onSelect,
  isCampSelected = false,
}: {
  dateRanges: DateRange[];
  daysAllowed?: number[];
  isFetching?: boolean;
  onSelect: () => void;
  isCampSelected?: boolean;
}) {
  const theme = useColorScheme();

  const getDayCellStyle = (day: string) => {
    for (const range of dateRanges) {
      const commonProps = range.id ? { id: range.id } : {};
      if (day === range.startDate) {
        return { rangeStart: true, ...commonProps };
      }
      if (day === range.endDate) {
        return { rangeEnd: true, ...commonProps };
      }
      if (range.dates.includes(day)) {
        return { range: true, ...commonProps };
      }
      // return commonProps;
    }
    return {};
  };

  const getDayStyle = (day: string) => {
    for (const range of dateRanges) {
      const dayOfWeek = new Date(day).getDay();
      if (day >= range.startDate && day <= range.endDate) {
        if (daysAllowed.length > 0 && !daysAllowed.includes(dayOfWeek)) {
          return { backgroundColor: '#eee', opacity: 0.5 };
        }
        return {
          backgroundColor: isCampSelected
            ? Colors[theme].accent
            : Colors[theme].backgroundSecondary,
        };
      }
    }
    return {};
  };

  const initialDate = dateRanges[0]?.startDate;

  return initialDate ? (
    <BookingCalendar
      displayMode="daily"
      onSelect={onSelect}
      getDayCellStyle={getDayCellStyle}
      getDayStyle={getDayStyle}
      initialDate={new Date(initialDate)}
      isDayAvailable={(d) =>
        dateRanges.some((range) => range.dates.includes(d))
      }
    />
  ) : isFetching ? (
    <ActivityIndicator />
  ) : null;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function addDays(dateString: string, days: number): string {
  const date = new Date(dateString);
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0]!;
}
